import { handleize, formatNostoErrorCode } from '@utilities/theme';
// Filter label/param helpers live in a vendor-neutral module so Shopify-only
// stores can render filters without loading Nosto. Re-exported below under their
// historical nosto* names for backward compatibility.
import {
  FILTER_PARAM_MAP,
  mapFieldToFilter as nostoMapFieldToFilter,
  transformFilterLabel as nostoTransformFilterLabel,
} from '@utilities/filter-labels';

// formatNostoErrorCode lives in utilities.js (vendor-neutral, no Nosto
// dependency) since vendor-agnostic error handlers call it without loading
// this module. Re-exported here for backward compatibility.
export { nostoMapFieldToFilter, nostoTransformFilterLabel, formatNostoErrorCode };

/**
 * NOSTO UTILITIES
 *
 * Comprehensive integration layer between Nosto's search/recommendation platform and Shopify's native APIs.
 * Provides seamless data transformation, query building, and vendor switching capabilities.
 *
 * Used by: product-grid.js, filter-drawer.js, predictive-search.js
 *
 * Key Features:
 * - Bidirectional field mapping between Nosto and Shopify formats
 * - Query building with client-specific preFilters (Greyson customizations)
 * - Data transformation for products, filters, and predictive search
 * - Vendor switching with automatic component reloading
 * - Error handling and fallback mechanisms
 * - Aggressive preloading to reduce LCP delays
 *
 * Client-Specific Customizations (Greyson):
 * - greyson-availability field filtering
 * - Custom color family mappings
 * - DataExtractor integration for enhanced metadata
 * - Brand-specific product hiding rules
 *
 * @namespace NostoUtilities
 */

/**
 * THE single global Nosto wait/timeout (ms). This is the one knob for "how long do we wait
 * for Nosto before giving up": the ceiling for the library-availability check
 * (nostoAvailableCheck), for an individual Nosto request, and for the PLP early-search primer
 * race in product-grid.js. If Nosto's library isn't ready (window.nostoLibraryLoaded === true)
 * within this window, the component's fetch rejects with LIBRARY_TIMEOUT and GC-2374 falls the
 * page back to Shopify. GC-2374 added that fallback, so this no longer needs to be generous —
 * a shorter ceiling means less time on a loading skeleton before falling back.
 *
 * Change this value ONLY here. The only intentional exceptions are predictive search's tighter
 * per-call overrides (2s search / 3s trending) so a slow autocomplete stays snappy; those are
 * emit-only + local-fallback and do not gate the page-wide deadline.
 * @type {number}
 */
export const NOSTO_DEFAULT_TIMEOUT_MS = 2200;

/**
 * Timestamped Nosto lifecycle logger, GATED behind Nosto debug mode so it never spams a
 * production console. Enable it by appending `?nostodebug=true` (or 1/yes/on) to the URL —
 * the same switch used elsewhere in the theme (theme.utilities.isNostoDebugEnabled()). When
 * on, every line is prefixed with elapsed ms since this module loaded (≈ page load) so the
 * sequence and gaps between Nosto events (preload → availability → primer → request →
 * fallback) are readable. No-op otherwise.
 * @param {...*} args
 */
const _nostoT0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
export const nostoLog = (...args) => {
  if (typeof window === 'undefined') return;
  if (window.theme?.utilities?.isNostoDebugEnabled?.() !== true) return;
  const elapsed = ((typeof performance !== 'undefined' && performance.now ? performance.now() : 0) - _nostoT0).toFixed(0);
  console.log(`[Nosto +${elapsed}ms]`, ...args);
};

// Global Nosto request queue for aggressive preloading
const _nostoRequestQueue = new Map();
let _nostoLibraryReady = false;
let _nostoReadyPromise = null;

// Library availability checking to prevent duplicate listeners
let _nostoAvailabilityCheck = null;

/**
 * Nosto Error Types for user-friendly error handling
 * @type {Object.<string, Object>}
 * @private
 */
const _nostoErrorTypes = {
  LIBRARY_TIMEOUT: {
    code: 'NOSTO_WRAPPER_LIBRARY_TIMEOUT',
    // Short, screenshot-friendly stem surfaced in the visible error ref line
    stem: 'LIB_TIMEOUT',
    message: 'Search is temporarily unavailable. Please refresh the page and try again.',
    userMessage: 'Search is temporarily unavailable. Please refresh the page and try again.',
    debugMessage: 'Nosto library failed to load within timeout'
  },
  REQUEST_TIMEOUT: {
    code: 'NOSTO_WRAPPER_REQUEST_TIMEOUT',
    stem: 'REQ_TIMEOUT',
    message: 'Loading is taking longer than expected. Please try again.',
    userMessage: 'Loading is taking longer than expected. Please try again.',
    debugMessage: 'Nosto API request timed out'
  },
  API_ERROR: {
    code: 'NOSTO_WRAPPER_API_ERROR',
    stem: 'API',
    message: 'Content is currently unavailable. Please try again in a moment.',
    userMessage: 'Content is currently unavailable. Please try again in a moment.',
    debugMessage: 'Nosto API returned an error'
  },
  NETWORK_ERROR: {
    code: 'NOSTO_WRAPPER_NETWORK_ERROR',
    stem: 'NET',
    message: 'Unable to connect to Nosto service. Please check your connection and try again.',
    userMessage: 'Unable to connect to Nosto service. Please check your connection and try again.',
    debugMessage: 'Network error occurred'
  },
  CONFIGURATION_ERROR: {
    code: 'NOSTO_WRAPPER_CONFIG_ERROR',
    stem: 'CONFIG',
    message: 'Configuration error. Please contact support if this persists.',
    userMessage: 'Configuration error. Please contact support if this persists.',
    debugMessage: 'Nosto configuration or query building error'
  }
};

/**
 * Classify a caught error as a network failure vs. a genuine API error.
 *
 * The Nosto client surfaces transport failures (blocked request, offline,
 * DNS/TLS failure) as the same rejected promise as real API errors, so a
 * screenshot can't tell "the request to nostojs never reached the server" from
 * "the server returned an error". This heuristic separates the two so the
 * visible ref line reads NET vs API.
 *
 * @param {Error|null} originalError - The underlying caught error, if any
 * @returns {boolean} True when the failure looks like a network/transport issue
 * @private
 */
const _looksLikeNetworkError = (originalError = null) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }

  const message = String(originalError?.message || '').toLowerCase();
  return /network|failed to fetch|load failed|networkerror|err_/.test(message);
};

/**
 * Create a Nosto error with user-friendly messaging
 * @param {string} errorType - Key from _nostoErrorTypes
 * @param {Object} [details] - Additional error details
 * @param {Error} [originalError] - Original error object for debugging
 * @returns {Error} - Enhanced error with user-friendly message
 * @private
 */
const _createNostoError = (errorType, details = {}, originalError = null) => {
  // Reclassify would-be API errors that are actually transport failures, so the
  // visible ref line and console both distinguish "request to nostojs failed"
  // from "Nosto returned an error".
  let resolvedType = errorType;
  if (errorType === 'API_ERROR' && _looksLikeNetworkError(originalError)) {
    resolvedType = 'NETWORK_ERROR';
  }

  const errorInfo = _nostoErrorTypes[resolvedType];
  if (!errorInfo) {
    // Fallback for unknown error types
    return new Error('An unexpected error occurred. Please try again.');
  }

  const error = new Error(errorInfo.message);

  // Add error metadata for debugging and handling
  error.code = errorInfo.code;
  error.stem = errorInfo.stem;
  error.userMessage = errorInfo.userMessage;
  error.debugMessage = errorInfo.debugMessage;
  error.details = details;
  error.originalError = originalError;

  // Preserve original error stack if available
  if (originalError?.stack) {
    error.stack = originalError.stack;
  }

  return error;
};

/**
 * Initialize Nosto preloading when library becomes available
 * @private
 */
const _initNostoPreloading = () => {
  if (_nostoLibraryReady) {
    console.log('Nosto Wrapper: Preloading already initialized');
    return;
  }

  nostoLog(`preload: init (timeout ${NOSTO_DEFAULT_TIMEOUT_MS}ms)`);
  const preloadStartTime = performance.now();

  _nostoReadyPromise = nostoAvailableCheck({ timeout: NOSTO_DEFAULT_TIMEOUT_MS })
    .then(() => {
      const elapsed = performance.now() - preloadStartTime;
      _nostoLibraryReady = true;
      nostoLog(`preload: library READY after ${elapsed.toFixed(0)}ms, draining ${Array.from(_nostoRequestQueue).length} queued`);

      // Process any queued requests
      for (const [key, { query, options, resolve, reject }] of _nostoRequestQueue) {
        _executeNostoRequest(key, query, options)
          .then(resolve)
          .catch(reject);
      }
      _nostoRequestQueue.clear();
      console.log('Nosto Wrapper: All queued requests processed');
    })
    .catch(error => {
      const elapsed = performance.now() - preloadStartTime;
      nostoLog(`preload: library FAILED after ${elapsed.toFixed(0)}ms, rejecting ${Array.from(_nostoRequestQueue).length} queued`);
      console.warn('Nosto Wrapper: Library failed to load, rejecting queued requests:', error);

      // Reject all queued requests
      for (const [key, { reject }] of _nostoRequestQueue) {
        reject(new Error('Nosto library failed to load within timeout'));
      }
      _nostoRequestQueue.clear();
    });
};

/**
 * Execute a Nosto request with caching
 * @param {string} cacheKey - Cache key for the request
 * @param {Object} query - Nosto query
 * @param {Object} options - Nosto options
 * @param {number} [timeout=NOSTO_DEFAULT_TIMEOUT_MS] - Request timeout in ms
 * @returns {Promise<Object>} Nosto response
 * @private
 */
const _executeNostoRequest = (cacheKey, query, options, timeout = NOSTO_DEFAULT_TIMEOUT_MS) => {
  return new Promise((resolve, reject) => {
    const startTime = performance.now();
    nostoLog(`request '${cacheKey}': START (timeout ${timeout}ms) — calling window.nostojs(api => api.search)`);

    const timeoutId = setTimeout(() => {
      const elapsed = performance.now() - startTime;
      nostoLog(`request '${cacheKey}': TIMED OUT after ${elapsed.toFixed(0)}ms → REQUEST_TIMEOUT (nostojs callback never fired = stub queue)`);
      const timeoutError = new Error(`Nosto request timed out after ${elapsed.toFixed(2)}ms`);
      reject(_createNostoError('REQUEST_TIMEOUT', { cacheKey, elapsed }, timeoutError));
    }, timeout);

    const cleanup = () => {
      clearTimeout(timeoutId);
    };

    window.nostojs((api) => {
      api
        .search(query, options)
        .then((response) => {
          const elapsed = performance.now() - startTime;
          console.log(`Nosto Wrapper: Request '${cacheKey}' completed successfully in ${elapsed.toFixed(2)}ms`);
          cleanup();
          resolve(response);
        })
        .catch((error) => {
          const elapsed = performance.now() - startTime;
          console.error(`Nosto Wrapper: Request '${cacheKey}' failed after ${elapsed.toFixed(2)}ms:`, error);
          cleanup();
          const apiError = new Error(`Nosto search failed: ${error.message}`);
          reject(_createNostoError('API_ERROR', { cacheKey, elapsed }, apiError));
        });
    });
  });
};

// Start preloading initialization immediately
if (typeof window !== 'undefined') {
  // Use requestIdleCallback if available, otherwise setTimeout
  const scheduleInit = window.requestIdleCallback ||
    ((callback) => setTimeout(callback, 1));

  scheduleInit(_initNostoPreloading);
}

/**
 * Build dynamic preFilter based on context and settings
 * @param {Object} options - Configuration options
 * @param {boolean} options.allowSoldOutStateProducts - Whether to include products tagged shopify:unavailable (default: false)
 * @returns {Array} - preFilter array for Nosto query
 * @private
 */
// DRIFT GUARD: the collection-page output of this function (categoryId present)
// is duplicated inline by the PLP early-search primer in
// shopify/snippets/product-grid.liquid. product-grid.js compares
// primer vs grid query via JSON.stringify, so any change here (conditions, key
// order, values) MUST be mirrored in the primer or the primer will mismatch and
// cause a duplicate search (visible via debug-mode console.warn in product-grid.js).
const _buildPreFilter = ({ allowSoldOutStateProducts = false, categoryId = null } = {}) => {
  const preFilterConditions = [
    // Always require greyson-availability to be true (product-level aggregate)
    // If product-level is true, it means at least one variant is available
    {
      field: "customFields.greyson-availability",
      value: ["true"]
    },
    // Always exclude products with the strict hide tags
    // While we move to extended variant support, let's stop Nosto from returning Complex Product and Child Product tagged products.
    {
      not: [{
        field: "tags1",
        value: [
          "nosto:hide",
          "theme:hide",
          "app only",
          "status:unlisted",
          "Complex Product",
          "Child Product"
        ]
      }]
    }
  ];
  // Conditionally exclude shopify:unavailable products if we explicitly exclude them.
  // The default behavior is to exclude sold out products.
  if (!allowSoldOutStateProducts) {
    // console.log("Extra Exclusion: Excluding sold out products from the Nosto query");
    preFilterConditions.push({
      not: [{
        field: "tags1",
        value: ["shopify:unavailable"]
      }]
    });
  }
  // Condiitonally exclude products with the tag `search:hide` if the categoryId is empty. Only a collection render would provide the categoryId.
  // Search page and predictive search bar should reject any products with the tag `search:hide`.
  if (!categoryId) {
    // console.log("Extra Exclusion: Excluding products with the tag `search:hide` from the Nosto query");
    preFilterConditions.push({
      not: [{
        field: "tags1",
        value: ["search:hide"]
      }]
    });
  }
  return [{
    all: preFilterConditions
  }];
};

/**
 * Default field selections for different Nosto query types
 * @type {Object.<string, Array<string>>}
 * @private
 */
const _defaultFields = {
  // DRIFT GUARD: `products` is duplicated inline by the PLP early-search primer
  // in shopify/snippets/product-grid.liquid.
  // Changes to the list or its ORDER must be mirrored there, or the primer
  // query comparison in product-grid.js will mismatch (duplicate search).
  products: [
    "pid",
    "name",
    "url",
    "imageUrl",
    "alternateImageUrls",
    "productId",
    "productType",
    "variantId",
    "price",
    "tags1",
    "customFields.key",
    "customFields.value",
    "skus.id",
    "skus.url",
    "skus.name",
    "skus.imageUrl",
    "skus.price",
    "skus.customFields.key",
    "skus.customFields.value",
  ],
  minimal: ["pid"], // For filter-only queries
  predictive: [
    "pid",
    "productId",
    "name",
    "url",
    "productType",
    "imageUrl",
    "alternateImageUrls",
    "variantId",
    "tags1",
    "customFields.key",
    "customFields.value",
    "skus.id",
    "skus.url",
    "skus.name",
    "skus.imageUrl",
    "skus.price",
    "skus.customFields.key",
    "skus.customFields.value",
  ],
  keywords: ["keyword"]
};

/**
 * Static sort mapping for better performance
 * Maps Shopify sort options to Nosto sort format
 * @type {Object.<string, Array<Object>>}
 * @private
 */
const _sortMapping = {
  "price-ascending": [{ field: "customFields.greyson-available_price_min", order: "asc" }],
  "price-descending": [{ field: "customFields.greyson-available_price_max", order: "desc" }],
};

/**
 * Cache for handle extraction to avoid repeated URL parsing
 * @type {Map}
 * @private
 */
let _handleCache;

/**
 * Cache for filter label transformations to avoid repeated processing
 * @type {Map}
 * @private
 */

/**
 * Cache for reverse filter mapping to avoid repeated object entries iteration
 * @type {Map}
 * @private
 */
let _reverseFilterMap;

/**
 * Transform Shopify boolean values to Nosto boolean values
 * @param {Array<string>} values - Array of filter values
 * @returns {Array<string>} - Transformed values with Shopify booleans converted to Nosto format
 * @private
 */
const transformShopifyBooleansToNosto = (values) => {
  const transformedValues = [];

  for (const value of values) {
    // Add the original value
    transformedValues.push(value);

    // Add transformed boolean values if applicable
    if (value === '1') {
      transformedValues.push('true');
    } else if (value === '0') {
      transformedValues.push('false');
    } else if (value === 'true') {
      transformedValues.push('1');
    } else if (value === 'false') {
      transformedValues.push('0');
    }
  }

  // Remove duplicates and return
  return [...new Set(transformedValues)];
};

/**
 * Wait for Nosto library to be available via global flag + custom event.
 *
 * Readiness is determined by either:
 * - `window.nostoLibraryLoaded === true` (immediate path)
 * - `3pa:nosto-library-loaded` dispatch on `window`/`document` (deferred path)
 *
 * @param {Object} options - Configuration options
 * @param {number} options.timeout - Overall timeout in ms (default: NOSTO_DEFAULT_TIMEOUT_MS)
 * @returns {Promise<boolean>} - Resolves when Nosto is available
 * @private
 */
const nostoAvailableCheck = ({ timeout = NOSTO_DEFAULT_TIMEOUT_MS } = {}) => {
  // Return existing promise if already checking
  if (_nostoAvailabilityCheck) {
    return _nostoAvailabilityCheck;
  }

  _nostoAvailabilityCheck = new Promise((resolve, reject) => {
    const isLibraryReady = () => {
      if (typeof window === 'undefined') {
        return false;
      }

      // Readiness is `window.nostoLibraryLoaded === true`, set ONLY by the real library
      // script's onload (see nosto-loading.liquid). Do NOT treat `typeof window.nostojs`
      // as readiness: nostojs is a command-queue STUB the theme defines synchronously
      // before the library loads, so it is defined even when the script is blocked/404s —
      // checking it caused a false "library available" and defeated the GC-2374 load-failure
      // detection. The real library drains window.nostojs.q; the stub leaves .q present.
      return window.nostoLibraryLoaded === true;
    };

    // Resolve immediately when the readiness flag is already set (real library loaded).
    if (isLibraryReady()) {
      nostoLog('availabilityCheck: library already available');
      _nostoAvailabilityCheck = null;
      resolve(true);
      return;
    }

    nostoLog(`availabilityCheck: WAITING for library (timeout ${timeout}ms)`);
    const startTime = performance.now();
    let timeoutId = null;
    let hasResolved = false;

    const cleanup = (listener) => {
      if (listener) {
        document.removeEventListener('3pa:nosto-library-loaded', listener);
        window.removeEventListener('3pa:nosto-library-loaded', listener);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      _nostoAvailabilityCheck = null;
    };

    const onLibraryLoaded = () => {
      if (hasResolved) return;
      if (!isLibraryReady()) return;

      hasResolved = true;
      const elapsed = performance.now() - startTime;
      nostoLog(`availabilityCheck: library became available after ${elapsed.toFixed(0)}ms (event)`);
      cleanup(onLibraryLoaded);
      resolve(true);
    };

    timeoutId = setTimeout(() => {
      const elapsed = performance.now() - startTime;
      nostoLog(`availabilityCheck: TIMED OUT after ${elapsed.toFixed(0)}ms → LIBRARY_TIMEOUT`);
      cleanup(onLibraryLoaded);
      const timeoutError = new Error(`Nosto library not available after ${elapsed.toFixed(2)}ms timeout`);
      reject(_createNostoError('LIBRARY_TIMEOUT', { elapsed, source: 'event_wait' }, timeoutError));
    }, timeout);

    document.addEventListener('3pa:nosto-library-loaded', onLibraryLoaded, { once: false });
    window.addEventListener('3pa:nosto-library-loaded', onLibraryLoaded, { once: false });
    onLibraryLoaded();
  });

  return _nostoAvailabilityCheck;
};

/**
 * Make a Nosto search API call with proper error handling and aggressive preloading
 * @param {Object} query - The Nosto search query
 * @param {Object} options - The Nosto search options
 * @param {Object} config - Configuration options
 * @param {number} config.timeout - Request timeout in ms (default: NOSTO_DEFAULT_TIMEOUT_MS)
 * @param {boolean} config.debug - Enable debug logging (default: false)
 * @param {string} config.trigger - The trigger for the Nosto search
 * @returns {Promise<Object>} - The Nosto response
 */
/**
 * Get the current Nosto library state
 * @returns {Object} State object with ready status and execution strategy
 * @private
 */
const _getNostoLibraryState = () => {
  // Check if library is fully ready. Gate on window.nostoLibraryLoaded (the real onload
  // signal), NOT `typeof window.nostojs` — nostojs is a stub that exists even on load
  // failure (see isLibraryReady in nostoAvailableCheck).
  if (_nostoLibraryReady && window.nostoLibraryLoaded === true) {
    return { ready: true, strategy: 'immediate' };
  }

  // Check if library is loading
  if (_nostoReadyPromise) {
    return { ready: false, strategy: 'wait_promise', promise: _nostoReadyPromise };
  }

  // Fallback: need to wait for library
  return { ready: false, strategy: 'wait_check' };
};

/**
 * Throw a LIBRARY_TIMEOUT immediately if the real Nosto library isn't loaded.
 *
 * Guards the request leg: after any availability wait, `window.nostojs` may still be only the
 * command-queue STUB (the library never loaded / was blocked). Calling `_executeNostoRequest`
 * in that state queues a callback that never fires and burns a full REQUEST_TIMEOUT — a second
 * needless 3s on top of the availability wait. We also can't rely on the preloader promise
 * rejecting: its `.catch` swallows the failure, so `_nostoReadyPromise` RESOLVES even when the
 * library failed. `window.nostoLibraryLoaded === true` (set only by the real script's onload)
 * is the authoritative signal, so gate on it here and fail fast.
 *
 * @param {string} trigger
 * @throws {Error} LIBRARY_TIMEOUT when the real library is not loaded
 * @private
 */
const _assertNostoLibraryLoaded = (trigger = '') => {
  if (window.nostoLibraryLoaded === true) return;
  nostoLog(`nostoSearch(${trigger}): library not loaded after wait → failing fast (LIBRARY_TIMEOUT)`);
  throw _createNostoError(
    'LIBRARY_TIMEOUT',
    { trigger, source: 'request_guard' },
    new Error('Nosto library not loaded when request was ready to execute'),
  );
};

export const nostoSearch = async (query, options, { timeout = NOSTO_DEFAULT_TIMEOUT_MS, debug = false, trigger = '' } = {}) => {
  // debug = true;

  if (debug) {
    console.log(`Nosto search query: ${trigger}`, query);
    console.log(`Nosto search options: ${trigger}`, options);
  }

  const state = _getNostoLibraryState();

  try {
    if (state.ready) {
      if (debug) {
        console.log(`Nosto Wrapper: Executing ${trigger} immediately (library ready)`);
      }
      return await _executeNostoRequest(`${trigger}_${Date.now()}`, query, options, timeout);
    }

    if (state.strategy === 'wait_promise') {
      if (debug) {
        console.log(`Nosto Wrapper: ${trigger} waiting for library promise`);
      }
      await state.promise;
      // The preloader promise resolves even on failure (its .catch swallows), so verify the
      // library is really loaded before issuing a request against the stub.
      _assertNostoLibraryLoaded(trigger);
      if (debug) {
        console.log(`Nosto Wrapper: ${trigger} library ready, executing now`);
      }
      return await _executeNostoRequest(`${trigger}_${Date.now()}`, query, options, timeout);
    }

    // Strategy: wait_check - use nostoAvailableCheck
    if (debug) {
      console.log(`Nosto Wrapper: ${trigger} waiting for library availability check`);
    }
    await nostoAvailableCheck({ timeout });
    _assertNostoLibraryLoaded(trigger);

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Nosto search request timed out after ${timeout}ms`));
      }, timeout);

      const cleanup = () => {
        clearTimeout(timeoutId);
      };

      window.nostojs((api) => {
        api
          .search(query, options)
          .then((response) => {
            cleanup();
            if (debug) {
              console.log(`Nosto search response: ${trigger}`, response);
            }
            resolve(response);
          })
          .catch((error) => {
            cleanup();
            // Preserve user-friendly error messages from _createNostoError
            if (error.code && error.userMessage) {
              reject(error); // Pass through the enhanced error object
            } else {
              const apiError = new Error(`Nosto search failed: ${trigger} ${error.message}`);
              reject(_createNostoError('API_ERROR', { trigger }, apiError));
            }
          });
      });
    });
  } catch (error) {
    // Preserve user-friendly error messages from _createNostoError
    if (error.code && error.userMessage) {
      throw error; // Re-throw the enhanced error object
    } else {
      throw _createNostoError('API_ERROR', { trigger }, error);
    }
  }
};

/**
 * Fetch Nosto's search session params directly from the JS API.
 *
 * Nosto now auto-injects sessionParams into `api.search()` requests, so
 * nostoSearch no longer passes them explicitly. This is exposed as
 * `window.getNostoSessionParams()` purely as a manual debug/QA tool — call it
 * in the console to compare what Nosto resolves for the current browser
 * against what it's auto-injecting into outgoing requests (e.g. via the
 * network tab), to verify the two stay 1:1.
 *
 * @param {Object} [options] - Passed through to api.getSearchSessionParams()
 * @returns {Promise<Object>} The session params Nosto would attach to a search request
 */
export const nostoGetSessionParams = (options = {}) =>
  new Promise((resolve, reject) => {
    if (typeof window.nostojs !== 'function') {
      reject(new Error('Nosto library is not available on this page'));
      return;
    }

    window.nostojs((api) => {
      api
        .getSearchSessionParams(options)
        .then(resolve)
        .catch(reject);
    });
  });

if (typeof window !== 'undefined') {
  // Logs the resolved data directly so a plain console call
  // (no `.then()`/`await`) shows the actual response, not a pending Promise.
  window.getNostoSessionParams = (options = {}) =>
    nostoGetSessionParams(options)
      .then((data) => {
        console.log('Nosto session params:', data);
        return data;
      })
      .catch((error) => {
        console.error('Nosto session params error:', error);
        throw error;
      });
}

/**
 * Resolve a recommendation campaign from a Nosto recommendations response.
 *
 * Supports the two common response shapes:
 * - response.recommendations[placementId]
 * - response.campaigns.recommendations[placementId]
 *
 * @param {Object} response - Nosto recommendations response object
 * @param {string} placementId - Placement id / element id
 * @returns {Object|null} Recommendation campaign object
 */
export const nostoGetRecommendationCampaign = (response, placementId = '') => {
  if (!response) return null;

  const recommendations =
    response?.recommendations ||
    response?.campaigns?.recommendations ||
    {};

  if (placementId && recommendations[placementId]) {
    return recommendations[placementId];
  }

  if (placementId) {
    const matchedCampaign = Object.values(recommendations).find((campaign) => {
      return campaign?.div_id === placementId;
    });

    if (matchedCampaign) {
      return matchedCampaign;
    }
  }

  return Object.values(recommendations)[0] || null;
};

/**
 * Normalize recommendation product URLs to storefront-relative paths.
 *
 * Nosto responses can vary between `url`, `product_url`, `link`, or absolute
 * canonical URLs. Product cards should always fetch product JSON from the
 * current storefront origin.
 *
 * @param {Object} product - Recommendation product object
 * @returns {string} Relative product URL path (or empty string when unavailable)
 */
const nostoNormalizeRecommendationProductUrl = (product = {}) => {
  const rawUrl = String(
    product?.url ||
      product?.productUrl ||
      product?.product_url ||
      product?.link ||
      product?.href ||
      '',
  ).trim();

  const fallbackHandle = String(
    product?.handle || product?.productHandle || product?.slug || '',
  ).trim();

  if (!rawUrl) {
    return fallbackHandle ? `/products/${fallbackHandle}` : '';
  }

  try {
    const baseOrigin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://example.com';
    const parsedUrl = new URL(rawUrl, baseOrigin);
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (error) {
    if (rawUrl.startsWith('/')) {
      return rawUrl;
    }
    return fallbackHandle ? `/products/${fallbackHandle}` : rawUrl;
  }
};

/**
 * Validate product-level availability flag for recommendation items.
 *
 * Recommendation responses can include product-level `custom_fields` where
 * `greyson-availability` indicates whether the item should be rendered.
 *
 * @param {Object} product - Recommendation product object
 * @returns {boolean} True when product-level availability is explicitly true
 */
const nostoIsRecommendationProductAvailable = (product = {}) => {
  const productCustomFields = product?.custom_fields || product?.customFields || {};
  const availabilityValue =
    productCustomFields?.['greyson-availability'] ??
    productCustomFields?.greysonAvailability ??
    '';

  if (availabilityValue === true) {
    return true;
  }

  return String(availabilityValue).trim().toLowerCase() === 'true';
};

/**
 * Resolve a valid default variant id for recommendation products.
 *
 * Recommendation payloads can include non-variant product-level fields like
 * `variant_id: "USD"`. We intentionally resolve from SKU-level ids and choose
 * the first SKU flagged as available (`available` or `greyson-availability`)
 * to mirror product-grid and predictive-search behavior.
 *
 * @param {Object} product - Recommendation product object
 * @returns {number|null} Variant id for product-card bootstrap
 */
const nostoResolveRecommendationDefaultVariantId = (product = {}) => {
  const parseCandidate = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const isTruthyFlag = (value) => {
    return value === true || String(value || '').toLowerCase() === 'true';
  };

  const skus = Array.isArray(product?.skus) ? product.skus : [];
  let firstNumericSkuId = null;

  for (const sku of skus) {
    const skuCandidates = [
      sku?.variant_id,
      sku?.variantId,
      sku?.id,
      sku?.sku_id,
      sku?.skuId,
    ];

    for (const candidate of skuCandidates) {
      const parsedCandidate = parseCandidate(candidate);
      if (parsedCandidate === null) {
        continue;
      }

      if (firstNumericSkuId === null) {
        firstNumericSkuId = parsedCandidate;
      }

      const customFields = sku?.custom_fields || sku?.customFields || {};
      const customAvailability =
        customFields?.['greyson-availability'] ||
        customFields?.greysonAvailability ||
        '';

      if (isTruthyFlag(sku?.available) || isTruthyFlag(customAvailability)) {
        return parsedCandidate;
      }
    }
  }

  return firstNumericSkuId;
};

const nostoParseVariantIdCandidate = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Parse a numeric variant id out of a Shopify ProductVariant GID.
 *
 * Nosto returns the product-level `greyson-first_greyson_available_variant`
 * customField as a GID string ("gid://shopify/ProductVariant/53130450370923").
 * Accepts a bare numeric id too, so callers don't have to care which form the
 * feed used.
 *
 * @param {string} value - GID string or bare numeric id
 * @returns {number|null} Numeric variant id, or null when unparseable
 */
const nostoParseVariantGid = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;

  // Take the trailing path segment so this works for a bare id as well as
  // any gid://shopify/ProductVariant/<id> form.
  const lastSegment = raw.split("/").pop();
  if (!/^\d+$/.test(lastSegment)) return null;

  const parsed = parseInt(lastSegment, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Resolve the merchandised sale variant for a sale collection.
 *
 * Sale collections (`greyson.prioritize_sale_variants`) forward-face sale
 * pricing. DataJet maintains `greyson.lowest_price_available_variant` /
 * `greyson.highest_price_available_variant` for exactly this, delivered by Nosto
 * as product-level `greyson-`prefixed customFields holding a ProductVariant GID.
 *
 * The default view and a low→high sort both want the lowest-priced variant; a
 * high→low sort wants the highest. Other sorts (featured, alphabetical, newest)
 * carry no price intent, so the caller skips this entirely for them.
 *
 * These are product-level fields, so they are blind to the per-SKU availability
 * gate. The metafield is the merchandising source of truth: DataJet computes it
 * from Liquid's view of the product, so when it is set we honour it even if the
 * referenced SKU did not clear the local availability gate. Nosto's availability
 * customField and the metafield are refreshed by separate syncs, so requiring
 * both to agree made the field silently inert whenever they were out of step.
 *
 * The one hard requirement is that the variant exists in `skus[]`. An id absent
 * from the feed cannot be resolved by `?variant=` downstream and would make the
 * card remove itself, so that case falls through to the plain SKU path.
 *
 * @param {Object} product - Nosto product hit
 * @param {Array} processedSkus - SKUs that passed the availability/filter gates
 * @param {boolean} wantHighest - True only for an explicit high→low price sort
 * @returns {number|null} Numeric variant id, or null to fall through
 */
const nostoResolveSaleVariantId = (
  product = {},
  processedSkus = [],
  wantHighest = false,
) => {
  const variantId = nostoParseVariantGid(
    nostoGetCustomFieldValue(
      product?.customFields,
      wantHighest
        ? "greyson-highest_price_available_variant"
        : "greyson-lowest_price_available_variant",
    ),
  );
  if (variantId === null) return null;

  // Prefer a SKU that already cleared the gates (keeps its resolved imageUrl),
  // but accept any SKU present in the feed — the metafield outranks the local
  // availability signal.
  if (processedSkus.some((sku) => parseInt(sku?.id, 10) === variantId)) {
    return variantId;
  }

  const skus = Array.isArray(product?.skus) ? product.skus : [];
  const existsInFeed = skus.some(
    (sku) => nostoGetSkuVariantId(sku) === variantId,
  );

  return existsInFeed ? variantId : null;
};

const nostoGetSkuVariantId = (sku = {}) => {
  const candidates = [
    sku?.variant_id,
    sku?.variantId,
    sku?.id,
    sku?.sku_id,
    sku?.skuId,
  ];
  for (let i = 0; i < candidates.length; i++) {
    const parsed = nostoParseVariantIdCandidate(candidates[i]);
    if (parsed !== null) return parsed;
  }
  return null;
};

const nostoSetImageWidth = (imageUrl = '', width = 540) => {
  const rawImageUrl = String(imageUrl || '').trim();
  if (!rawImageUrl) return '';

  const filesSplit = rawImageUrl.split('/files/');
  const normalizedImageUrl = filesSplit.length > 1
    ? `https://greysonclothiers.com/cdn/shop/files/${filesSplit[filesSplit.length - 1]}`
    : rawImageUrl;

  const hashIndex = normalizedImageUrl.indexOf('#');
  const hasHash = hashIndex !== -1;
  const hash = hasHash ? normalizedImageUrl.slice(hashIndex) : '';
  const urlWithoutHash = hasHash ? normalizedImageUrl.slice(0, hashIndex) : normalizedImageUrl;

  const queryIndex = urlWithoutHash.indexOf('?');
  const baseUrl = queryIndex === -1 ? urlWithoutHash : urlWithoutHash.slice(0, queryIndex);
  const queryString = queryIndex === -1 ? '' : urlWithoutHash.slice(queryIndex + 1);

  const params = new URLSearchParams(queryString);
  params.set('width', String(width));

  const nextQuery = params.toString();
  const querySuffix = nextQuery ? `?${nextQuery}` : '';

  return `${baseUrl}${querySuffix}${hash}`;
};

const nostoGetVariantImageUrl = (product = {}, defaultVariantId = null) => {
  const targetVariantId = nostoParseVariantIdCandidate(defaultVariantId);
  const skus = Array.isArray(product?.skus) ? product.skus : [];

  if (targetVariantId !== null) {
    for (let i = 0; i < skus.length; i++) {
      const sku = skus[i];
      const skuVariantId = nostoGetSkuVariantId(sku);
      if (skuVariantId !== targetVariantId) continue;

      const skuImageUrl = String(
        sku?.imageUrl || sku?.image_url || sku?.image || '',
      ).trim();
      if (skuImageUrl) return nostoSetImageWidth(skuImageUrl);
      break;
    }
  }

  return nostoSetImageWidth(
    String(product?.imageUrl || product?.image_url || '').trim(),
  );
};

const nostoGetCustomFieldValue = (customFields = null, key = "") => {
  if (!key) return "";

  if (Array.isArray(customFields)) {
    for (let i = 0; i < customFields.length; i++) {
      const field = customFields[i];
      if (String(field?.key || "") !== key) continue;
      return String(field?.value || "").trim();
    }
    return "";
  }

  if (customFields && typeof customFields === "object") {
    return String(customFields[key] || "").trim();
  }

  return "";
};

/**
 * Parse a Nosto customField that holds a JSON-encoded string array
 * (e.g. greyson-available_colors = '["WOLF BLUE","ARCTIC"]').
 *
 * @param {Array|Object} customFields - Nosto customFields
 * @param {string} key - The customField key
 * @returns {string[]} Parsed array (empty when absent/unparseable)
 */
const nostoParseJsonArrayField = (customFields, key) => {
  const raw = nostoGetCustomFieldValue(customFields, key);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map((value) => String(value)) : [];
  } catch (_error) {
    return [];
  }
};

/**
 * Parse a Nosto money customField (e.g. greyson-available_price_min_money =
 * '{"amount":"160.00","currency_code":"USD"}') into { amount, currency }.
 *
 * @param {Array|Object} customFields - Nosto customFields
 * @param {string} key - The customField key
 * @returns {{ amount: number|null, currency: string }}
 */
const nostoParseMoneyField = (customFields, key) => {
  const raw = nostoGetCustomFieldValue(customFields, key);
  if (!raw) return { amount: null, currency: "" };
  try {
    const parsed = JSON.parse(raw);
    const amount = parseFloat(parsed?.amount);
    return {
      amount: Number.isFinite(amount) ? amount : null,
      currency: String(parsed?.currency_code || ""),
    };
  } catch (_error) {
    return { amount: null, currency: "" };
  }
};

/**
 * Resolve a per-color swatch image for a Nosto product.
 *
 * Nosto SKUs now return their own `imageUrl` directly (the authoritative,
 * variant-linked asset — see reference/product-cards/nosto-product-return.json)
 * so that is always tier 1. The filename-matching tiers below are a fallback
 * for older/partial Nosto payloads (or SKUs missing imageUrl) where we only have
 * the hit's `alternateImageUrls` (full CDN URLs) and a `skucode` customField
 * (e.g. "MCLSCK64-476-S") to go on. Greyson names media files
 * `<base>_<colorcode>_<n>...` where the middle segment is the color code.
 *
 * Priority:
 *   0. sku.imageUrl (authoritative — direct from Nosto)
 *   1. base + colorcode exact prefix match in mediaUrls (most specific)
 *   2. colorcode-only middle segment match in mediaUrls (any base)
 *   3. fallbackUrl (hit-level product image)
 *
 * @param {string} skucode - The SKU's skucode customField
 * @param {string[]} mediaUrls - hit.alternateImageUrls (NOT including hit.imageUrl — see fallbackUrl)
 * @param {string} fallbackUrl - Hit-level image URL used when nothing else matches
 * @param {number} [width=100] - Width to size the resolved image to
 * @param {string} [skuImageUrl=""] - The SKU's own imageUrl, when Nosto provides one
 * @returns {string} A width-sized CDN URL
 */
const nostoResolveColorImage = (
  skucode,
  mediaUrls,
  fallbackUrl,
  width = 100,
  skuImageUrl = "",
) => {
  const trimmedSkuImageUrl = String(skuImageUrl || "").trim();
  if (trimmedSkuImageUrl) return nostoSetImageWidth(trimmedSkuImageUrl, width);

  const parts = String(skucode || "").toLowerCase().split(/[-_]/);
  const base = parts[0] || "";
  const colorCode = parts[1] || "";

  if (colorCode && Array.isArray(mediaUrls)) {
    const filenameOf = (url) =>
      String(url || "").split("/").pop().split("?")[0].toLowerCase();
    // Tier 1: base + colorcode exact prefix.
    if (base) {
      const exact = mediaUrls.find((url) => {
        const filename = filenameOf(url);
        return (
          filename.startsWith(`${base}_${colorCode}_`) ||
          filename.startsWith(`${base}-${colorCode}-`)
        );
      });
      if (exact) return nostoSetImageWidth(exact, width);
    }
    // Tier 2: colorcode-only middle segment (any base).
    const colorCodePattern = new RegExp(`^[a-z0-9]+[-_]${colorCode}[-_]`);
    const byColor = mediaUrls.find((url) => colorCodePattern.test(filenameOf(url)));
    if (byColor) return nostoSetImageWidth(byColor, width);
  }

  return nostoSetImageWidth(fallbackUrl, width);
};

/**
 * Find a Nosto SKU by its (Shopify) variant id.
 * @param {Object} hit - A Nosto product hit
 * @param {number} variantId
 * @returns {Object|null}
 */
const nostoFindSkuByVariantId = (hit, variantId) => {
  const skus = Array.isArray(hit?.skus) ? hit.skus : [];
  const target = parseInt(variantId, 10);
  if (!Number.isFinite(target)) return null;
  return skus.find((sku) => parseInt(sku?.id, 10) === target) || null;
};

/**
 * Build up to `maxSwatches` normalized swatch objects for a Nosto hit, matching
 * the CardPayload contract consumed by product-grid.js / product-card.js.
 *
 * Swatches are selected in available_colors order, skipping hide_colors. Per
 * color we use the first matching SKU as the representative variant and resolve
 * a per-color image via skucode matching. `option_value_id` is null (Nosto has
 * no Shopify option-value ids) — the card falls back to color-handle matching
 * and the deferred product-json fetch supplies the real ids.
 *
 * @param {Object} hit - A Nosto product hit
 * @param {Object} opts
 * @param {string[]} opts.saleColors
 * @param {string[]} opts.hideColors
 * @param {string[]} opts.mediaUrls - [hit.imageUrl, ...hit.alternateImageUrls]
 * @param {string} [opts.defaultColor] - Default variant color, rendered first
 * @param {number} [opts.maxSwatches=3]
 * @returns {Array<Object>} Normalized swatch objects
 */
const nostoBuildCardSwatches = (
  hit,
  { saleColors = [], hideColors = [], mediaUrls = [], defaultColor = "", maxSwatches = 3 } = {},
) => {
  const skus = Array.isArray(hit?.skus) ? hit.skus : [];
  if (!skus.length) return [];

  // Index the first SKU per color (skip hidden colors).
  const hideSet = new Set(hideColors.map((color) => String(color).toLowerCase()));
  const saleSet = new Set(saleColors.map((color) => String(color).toLowerCase()));
  const firstSkuByColor = new Map();

  for (let i = 0; i < skus.length; i++) {
    const sku = skus[i];
    const color =
      nostoGetCustomFieldValue(sku?.customFields, "greyson-available_color") ||
      nostoGetCustomFieldValue(sku?.customFields, "color");
    if (!color) continue;
    const colorKey = color.toLowerCase();
    if (hideSet.has(colorKey)) continue;
    if (!firstSkuByColor.has(colorKey)) {
      firstSkuByColor.set(colorKey, { color, sku });
    }
  }

  // Order: default-variant color FIRST (mirrors the hydrated card's selected-
  // swatch-first), then SKU discovery order — which is the variant/merchandising
  // order, NOT the alphabetical greyson-available_colors list. (availableColors
  // is used only as a membership/availability set elsewhere.) Dedup via `seen`.
  const discoveryOrder = Array.from(firstSkuByColor.values()).map(
    (entry) => entry.color,
  );
  const orderedColors = defaultColor
    ? [defaultColor, ...discoveryOrder]
    : discoveryOrder;

  const swatches = [];
  const seen = new Set();

  for (let i = 0; i < orderedColors.length && swatches.length < maxSwatches; i++) {
    const color = String(orderedColors[i]);
    const colorKey = color.toLowerCase();
    if (seen.has(colorKey) || hideSet.has(colorKey)) continue;
    const entry = firstSkuByColor.get(colorKey);
    if (!entry) continue;
    seen.add(colorKey);

    const { sku } = entry;
    const available =
      nostoGetCustomFieldValue(sku?.customFields, "greyson-availability") === "true";
    // Mirror the card's swatch logic: never render an unavailable swatch. The
    // color may sit in available_colors at the product level yet have its
    // representative SKU unavailable — skip it and let the next color take the slot.
    if (!available) continue;

    const skucode = nostoGetCustomFieldValue(sku?.customFields, "skucode");
    const colorFamily =
      nostoGetCustomFieldValue(sku?.customFields, "greyson-color_family") ||
      nostoGetCustomFieldValue(sku?.customFields, "greyson-available_color_family");
    const isSale =
      saleSet.has(colorKey) ||
      nostoGetCustomFieldValue(sku?.customFields, "greyson-on_sale") === "true";
    const variantId = parseInt(sku?.id, 10);
    const fallbackUrl = String(hit?.imageUrl || "");
    const skuImageUrl = String(sku?.imageUrl || "").trim();

    swatches.push({
      option_value_id: null,
      color,
      color_family: colorFamily,
      handle: handleize(color),
      url: String(hit?.url || ""),
      swatch_image: nostoResolveColorImage(skucode, mediaUrls, fallbackUrl, 100, skuImageUrl),
      swatch_image_aspect_ratio: null,
      // Per-color main image so a pre-fetch swatch click can swap before the
      // deferred product-json fetch resolves the full media gallery.
      selected_image: nostoResolveColorImage(skucode, mediaUrls, fallbackUrl, 540, skuImageUrl),
      available,
      isSale: isSale,
      variant_id: Number.isFinite(variantId) ? variantId : null,
    });
  }

  return swatches;
};

/**
 * Transform a Nosto recommendation campaign into the theme's product format.
 *
 * @param {Object} campaign - Nosto recommendation campaign object
 * @returns {Array<Object>} Array of normalized product objects
 */
export const nostoTransformRecommendationCampaignToProducts = (campaign = {}) => {
  const recommendationProducts = Array.isArray(campaign?.products)
    ? campaign.products
    : [];

  const normalizedProducts = [];

  for (let i = 0; i < recommendationProducts.length; i++) {
    const product = recommendationProducts[i];

    if (!nostoIsRecommendationProductAvailable(product)) {
      continue;
    }

    const productId = parseInt(
      product?.productId || product?.product_id || product?.id || product?.pid,
      10,
    );

    if (!Number.isFinite(productId)) {
      continue;
    }

    const url = nostoNormalizeRecommendationProductUrl(product);
    const handle = product?.handle || product?.productHandle || extractHandleFromUrl(url);

    if (!url) {
      continue;
    }

    const defaultVariantId = nostoResolveRecommendationDefaultVariantId(product);
    const imageUrl = nostoGetVariantImageUrl(product, defaultVariantId);
    const subtitle = nostoGetCustomFieldValue(
      product?.custom_fields || product?.customFields,
      "custom-subtitle",
    );

    normalizedProducts.push({
      id: productId,
      handle,
      title: product?.name || product?.title || '',
      subtitle,
      url,
      imageUrl,
      defaultVariantId: Number.isFinite(defaultVariantId)
        ? defaultVariantId
        : null,
      tags: Array.isArray(product?.tags1)
        ? product.tags1
        : Array.isArray(product?.tags)
          ? product.tags
          : [],
    });
  }

  return normalizedProducts;
};

/**
 * Request recommendation data from Nosto's recommendation request API.
 *
 * Uses createRecommendationRequest() for non-SPA theme usage and returns
 * normalized products that match existing theme product-card contracts.
 *
 * @param {Object} config - Request config
 * @param {string} config.placementId - Recommendation placement id
 * @param {number|string} config.productId - Current product id
 * @param {string} [config.intent] - Recommendation intent (related/complementary)
 * @param {number} [config.limit] - Max recommendation products
 * @param {boolean} [config.debug] - Debug logging
 * @param {number} [config.timeout] - Timeout in ms
 * @returns {Promise<Object>} Normalized recommendation response
 */
export const nostoCreateRecommendationRequest = async ({
  placementId = '',
  productId,
  intent = 'related',
  limit = 10,
  debug = false,
  timeout = NOSTO_DEFAULT_TIMEOUT_MS,
} = {}) => {
  if (!placementId) {
    throw _createNostoError(
      'CONFIGURATION_ERROR',
      { placementId },
      new Error('Nosto recommendation placement id is required'),
    );
  }

  if (!productId) {
    throw _createNostoError(
      'CONFIGURATION_ERROR',
      { productId },
      new Error('Nosto recommendation product id is required'),
    );
  }

  await nostoAvailableCheck({ timeout });

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      const timeoutError = new Error(
        `Nosto recommendation request timed out after ${timeout}ms`,
      );
      reject(_createNostoError('REQUEST_TIMEOUT', { placementId, productId }, timeoutError));
    }, timeout);

    const cleanup = () => {
      clearTimeout(timeoutId);
    };

    window.nostojs((api) => {
      try {
        const requestBuilder = api
          .createRecommendationRequest({ includeTagging: true })
          .setPageType('product')
          .setProducts([{ product_id: String(productId) }])
          .setElements([placementId])
          .setResponseMode('JSON_ORIGINAL');

        if (debug) {
          console.log('Nosto recommendation request config', {
            placementId,
            productId: String(productId),
            intent,
            limit,
          });
        }

        requestBuilder
          .load({ includeTagging: true })
          .then((response) => {
            cleanup();

            const campaign = nostoGetRecommendationCampaign(response, placementId);
            const transformedProducts =
              nostoTransformRecommendationCampaignToProducts(campaign);
            const products = transformedProducts.slice(0, Math.max(1, limit));

            if (debug) {
              const missingUrlCount = transformedProducts.filter((product) => !product.url).length;
              const missingVariantCount = transformedProducts.filter(
                (product) => !Number.isFinite(product.defaultVariantId),
              ).length;
              console.log('Nosto recommendation response', {
                placementId,
                resultId: campaign?.result_id || null,
                totalProducts: transformedProducts.length,
                returnedProducts: products.length,
                missingUrlCount,
                missingVariantCount,
                sampleUrls: products.slice(0, 3).map((product) => product.url),
                sampleVariantIds: products
                  .slice(0, 3)
                  .map((product) => product.defaultVariantId),
              });
            }

            resolve({
              vendor: 'nosto',
              placementId,
              resultId: campaign?.result_id || null,
              campaign,
              products,
            });
          })
          .catch((error) => {
            cleanup();
            const apiError = new Error(
              `Nosto recommendation request failed: ${error?.message || 'Unknown error'}`,
            );
            reject(
              _createNostoError(
                'API_ERROR',
                { placementId, productId: String(productId), intent },
                apiError,
              ),
            );
          });
      } catch (error) {
        cleanup();
        const requestError = new Error(
          `Failed to build Nosto recommendation request: ${error?.message || 'Unknown error'}`,
        );
        reject(
          _createNostoError(
            'CONFIGURATION_ERROR',
            { placementId, productId: String(productId), intent },
            requestError,
          ),
        );
      }
    });
  });
};

/**
 * Register Nosto's campaign click attribution listener for a rendered campaign container.
 *
 * This should be called when rendering recommendation results from JSON mode.
 *
 * @param {HTMLElement} element - The campaign container element
 * @param {Object} campaign - Campaign object from Nosto response
 * @param {Object} options - Configuration options
 * @param {boolean} [options.debug=false] - Enable debug logging
 */
export const nostoAttributeProductClicksInCampaign = async (
  element,
  campaign,
  { debug = false } = {},
) => {
  if (!element || !campaign) return;

  await nostoAvailableCheck();

  window.nostojs((api) => {
    try {
      api.attributeProductClicksInCampaign(element, campaign);
      if (debug) {
        console.log('Nosto campaign click attribution attached', {
          resultId: campaign?.result_id || null,
          divId: campaign?.div_id || null,
        });
      }
    } catch (error) {
      if (debug) {
        console.error('Failed to attach Nosto campaign click attribution', error);
      }
    }
  });
};

/**
 * Record attribution event with Nosto
 * Core wrapper for api.recordAttribution() with proper error handling
 *
 * @see https://docs.nosto.com/techdocs/apis/frontend/js-apis/record-attribution
 *
 * @param {Object} options - Attribution options
 * @param {string} options.type - Event type (e.g., "vp", "cp", "is", "vc"). See Nosto docs for full list.
 * @param {string} options.target - Target ID (product ID, search term, category ID)
 * @param {string} [options.ref] - Recommendation/slot ID that contained the link which triggered the event
 * @param {string} [options.refType] - Type of Nosto feature that contributed to the attribution (e.g., "cmp", "os", "rec", "api")
 * @param {string} [options.refSrc] - Product ID of parent element of the link that triggered the event
 * @param {string} [options.targetFragment] - The skuId in case of "vp" events
 * @param {boolean} [options.debug=false] - Enable debug logging
 * @param {number} [options.timeout=NOSTO_DEFAULT_TIMEOUT_MS] - Request timeout in ms
 * @returns {Promise<void>} - Resolves when attribution is recorded (non-blocking)
 */
export const nostoRecordAttribution = async ({ type, target, ref = null, refType = null, refSrc = null, targetFragment = null, debug = false, timeout = NOSTO_DEFAULT_TIMEOUT_MS } = {}) => {
  // debug = true;
  try {
    // Wait for Nosto to be available
    await nostoAvailableCheck({ timeout });

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Nosto attribution request timed out after ${timeout}ms`));
      }, timeout);

      const cleanup = () => {
        clearTimeout(timeoutId);
      };

      if (debug) {
        console.log(`Nosto attribution: ${type}`, { type, target, ref, refType, refSrc, targetFragment });
      }

      window.nostojs((api) => {
        const attributionParams = { type, target };
        if (ref) {
          attributionParams.ref = ref;
        }
        if (refType) {
          attributionParams.refType = refType;
        }
        if (refSrc) {
          attributionParams.refSrc = refSrc;
        }
        if (targetFragment) {
          attributionParams.targetFragment = targetFragment;
        }

        try {
          api
            .recordAttribution(attributionParams)
            .done(() => {
              cleanup();
              if (debug) {
                console.log(`Nosto attribution recorded: ${type}`, { type, target, ref, refType, refSrc, targetFragment });
              }
              resolve();
            });
        } catch (error) {
          cleanup();
          reject(new Error(`Nosto attribution failed for type '${type}': ${error.message || 'Unknown error'}`));
        }
      });
    });
  } catch (error) {
    throw new Error(`Nosto attribution failed for type '${type}': ${error.message}`);
  }
};

/**
 * Record search add-to-cart event with Nosto
 * Use this when a product is added to cart directly from search/category/autocomplete results
 * without navigating to the product page first.
 *
 * @see https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
 *
 * @param {string} type - Search type: "serp", "autocomplete", or "category"
 * @param {Object} hit - Product hit object
 * @param {string} hit.productId - The product ID
 * @param {string} hit.url - The product URL
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.debug=false] - Enable debug logging
 * @param {number} [options.timeout=NOSTO_DEFAULT_TIMEOUT_MS] - Request timeout in ms
 * @returns {Promise<void>} - Resolves when tracking is recorded (non-blocking)
 */
export const nostoRecordSearchAddToCart = async (type, hit, { debug = false, timeout = NOSTO_DEFAULT_TIMEOUT_MS } = {}) => {
  // debug = true;
  try {
    await nostoAvailableCheck({ timeout });

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Nosto recordSearchAddToCart request timed out after ${timeout}ms`));
      }, timeout);

      const cleanup = () => {
        clearTimeout(timeoutId);
      };

      window.nostojs((api) => {
        try {
          api.recordSearchAddToCart(type, hit);
          cleanup();
          if (debug) {
            console.log(`Nosto recordSearchAddToCart recorded: ${type}`, hit);
          }
          resolve();
        } catch (error) {
          cleanup();
          reject(new Error(`Nosto recordSearchAddToCart failed for type '${type}': ${error.message || 'Unknown error'}`));
        }
      });
    });
  } catch (error) {
    throw new Error(`Nosto recordSearchAddToCart failed for type '${type}': ${error.message}`);
  }
};

/**
 * Record search product click event with Nosto
 * Use this when a user clicks on a product in search/category/autocomplete results.
 *
 * @see https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
 *
 * @param {string} type - Search type: "serp", "autocomplete", or "category"
 * @param {Object} hit - Product hit object
 * @param {string} hit.productId - The product ID
 * @param {string} hit.url - The product URL
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.debug=false] - Enable debug logging
 * @param {number} [options.timeout=NOSTO_DEFAULT_TIMEOUT_MS] - Request timeout in ms
 * @returns {Promise<void>} - Resolves when tracking is recorded (non-blocking)
 */
export const nostoRecordSearchClick = async (type, hit, { debug = false, timeout = NOSTO_DEFAULT_TIMEOUT_MS } = {}) => {
  // debug = true;
  try {
    await nostoAvailableCheck({ timeout });

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Nosto recordSearchClick request timed out after ${timeout}ms`));
      }, timeout);

      const cleanup = () => {
        clearTimeout(timeoutId);
      };

      if (debug) {
        console.log(`Nosto recordSearchClick: ${type}`, hit);
      }

      window.nostojs((api) => {
        try {
          api.recordSearchClick(type, hit);
          cleanup();
          if (debug) {
            console.log(`Nosto recordSearchClick recorded: ${type}`, hit);
          }
          resolve();
        } catch (error) {
          cleanup();
          reject(new Error(`Nosto recordSearchClick failed for type '${type}': ${error.message || 'Unknown error'}`));
        }
      });
    });
  } catch (error) {
    throw new Error(`Nosto recordSearchClick failed for type '${type}': ${error.message}`);
  }
};

/**
 * Record search form submit event with Nosto
 * Use this to mark a search as "organic" - called BEFORE the search request.
 *
 * @see https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
 *
 * @param {string} query - The search query
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.debug=false] - Enable debug logging
 * @param {number} [options.timeout=NOSTO_DEFAULT_TIMEOUT_MS] - Request timeout in ms
 * @returns {Promise<void>} - Resolves when tracking is recorded (non-blocking)
 */
export const nostoRecordSearchSubmit = async (query, { debug = false, timeout = NOSTO_DEFAULT_TIMEOUT_MS } = {}) => {
  // debug = true;
  try {
    await nostoAvailableCheck({ timeout });

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Nosto recordSearchSubmit request timed out after ${timeout}ms`));
      }, timeout);

      const cleanup = () => {
        clearTimeout(timeoutId);
      };

      if (debug) {
        console.log(`Nosto recordSearchSubmit:`, query);
      }

      window.nostojs((api) => {
        try {
          api.recordSearchSubmit(query);
          cleanup();
          if (debug) {
            console.log(`Nosto recordSearchSubmit recorded:`, query);
          }
          resolve();
        } catch (error) {
          cleanup();
          reject(new Error(`Nosto recordSearchSubmit failed: ${error.message || 'Unknown error'}`));
        }
      });
    });
  } catch (error) {
    throw new Error(`Nosto recordSearchSubmit failed: ${error.message}`);
  }
};

/**
 * Map Shopify sort options to Nosto sort format
 * @param {string} shopifySort - Shopify sort option (e.g., "price-ascending", "price-descending")
 * @returns {Array|null} - Nosto sort array or null for default relevance
 * @private
 */
const mapShopifySortToNosto = (shopifySort) => {
  // For Nosto, only handle price sorting - all other options use default relevance
  return _sortMapping[shopifySort] || null;
};

/**
 * Build base Nosto query configuration
 * @param {Object} config - Configuration object
 * @param {string} config.context - "search" or "collection"
 * @param {string} config.terms - Search terms (for search context)
 * @param {string|number} config.id - Collection ID (for collection context)
 * @param {string} config.type - Query type: "products", "filters", "predictive"
 * @param {number} config.size - Number of results to return (default: 24)
 * @param {number} config.from - Starting offset (default: 0)
 * @param {Array} config.filters - Additional filters to apply
 * @param {string} config.sortBy - Sort option (default: "relevance")
 * @returns {Object} - Nosto query configuration
 *
 * DRIFT GUARD: the collection branch's query/options shape (including KEY ORDER)
 * is duplicated by the PLP early-search primer in shopify/snippets/product-grid.liquid.
 * Keep both in sync.
 */
export const nostoBuildQuery = ({
  context,
  terms = "",
  categoryId = null,
  type = "products",
  size = 50,
  from = 0,
  filters = [],
  sortBy = "relevance",
  allowSoldOutStateProducts = false
}) => {
  const baseProductsConfig = {
    facets: ["*"],
    fields: _defaultFields[type] || _defaultFields.products,
    size: type === "filters" ? 1 : size, // Minimal size for filter-only queries
    from,
    filter: filters,
    preFilter: _buildPreFilter({ allowSoldOutStateProducts, categoryId: categoryId })
  };

  // Add sorting to the query if not relevance (default)
  if (sortBy && sortBy !== "relevance") {
    const nostoSort = mapShopifySortToNosto(sortBy);
    if (nostoSort) {
      baseProductsConfig.sort = nostoSort;
    }
  }

  if (context === "collection") {
    return {
      query: {
        products: {
          categoryId: String(categoryId),
          ...baseProductsConfig,
        },
      },
      options: { track: "category", redirect: true },
    };
  } else if (context === "search") {
    const baseQuery = {
      query: terms.trim().toLowerCase(),
      products: baseProductsConfig,
    };

    // Add keywords for predictive search
    if (type === "predictive") {
      baseQuery.keywords = {
        fields: _defaultFields.keywords,
        size: 10,
      };
    }

    const queryOptions = { track: type === "predictive" ? "autocomplete" : "serp" };
    // get query params and look for `is_search_suggestion=true`
    const urlParams = new URLSearchParams(window.location.search);
    const isSearchSuggestion = urlParams.get("is_search_suggestion") === "true";
    if (isSearchSuggestion) {
      queryOptions.isKeyword = true;
    }

    // Only allow the redirect if we are not in the predicitve search results UI
    if (type !== "predictive") {
      queryOptions.redirect = true;
    }

    return {
      query: baseQuery,
      options: queryOptions,
    };
  }

  throw _createNostoError('CONFIGURATION_ERROR', { context }, new Error(`Unsupported context: ${context}`));
};

/**
 * Map Shopify filter parameter to Nosto field name (reverse lookup)
 * @param {string} shopifyFilterParam - Shopify filter parameter
 * @returns {string|null} - Nosto field name or null if not found
 */
export const nostoMapShopifyFilterToNosto = (shopifyFilterParam) => {
  // Create reverse mapping cache on first access for O(1) lookups
  if (!_reverseFilterMap) {
    _reverseFilterMap = new Map();
    for (const [nostoField, shopifyParam] of Object.entries(FILTER_PARAM_MAP)) {
      _reverseFilterMap.set(shopifyParam, nostoField);
    }
  }

  return _reverseFilterMap.get(shopifyFilterParam) || null;
};

/**
 * Build Nosto filters from Shopify filter parameters
 * @param {Object} activeFilters - Current active filters in Shopify format
 * @param {Object} options - Configuration options
 * @param {Array} options.excludeFilters - Filter parameter names to exclude
 * @param {boolean} options.debug - Enable debug logging
 * @returns {Array} - Nosto filter structure: [] or [{ all: [filter objects] }]
 */
export const nostoBuildFiltersFromShopify = (activeFilters = {}, { excludeFilters = [], debug = false } = {}) => {
  const defaultExcludeFilters = [];

  // Create exclude set for O(1) lookups instead of O(n) array.includes()
  const allExcludeFilters = new Set([...defaultExcludeFilters, ...excludeFilters]);

  // Use Map for O(1) filter lookups instead of O(n) array.find()
  const filtersMap = new Map();

  if (debug) {
    console.log("Building Nosto filters from Shopify activeFilters:", activeFilters);
  }

  // Note: greyson-availability filtering is handled via preFilter in _buildPreFilter()
  // and should not be applied to the main filter query param
  const greysonAvailabilityNostoField = 'skus.customFields.greyson-availability';

  // Convert activeFilters to Nosto format
  for (const [key, values] of Object.entries(activeFilters)) {
    if (Array.isArray(values) && values.length > 0 && !allExcludeFilters.has(key)) {
      // Map Shopify filter parameters to Nosto field names
      const nostoField = nostoMapShopifyFilterToNosto(key);
      if (nostoField) {
        // Transform Shopify boolean values to Nosto format
        const transformedValues = transformShopifyBooleansToNosto(values);

        // Skip greyson-availability filter - it's handled via preFilter
        if (nostoField === greysonAvailabilityNostoField) {
          // Skip adding this filter to the main filter query param
          continue;
        }

        filtersMap.set(nostoField, {
          field: nostoField,
          value: transformedValues,
        });
      }
    }
  }

  const filterConditions = Array.from(filtersMap.values());

  if (debug) {
    console.log("Built Nosto filter conditions:", filterConditions);
    console.log("Note: greyson-availability filtering is handled via preFilter, not main filter query param");
  }

  // Return filters wrapped in 'all' object structure to match preFilter format
  // If no filters, return empty array to avoid sending empty 'all' object
  if (filterConditions.length === 0) {
    return [];
  }

  return filterConditions;
};

/**
 * Extract handle from product URL
 * @param {string} url - Product URL
 * @returns {string} - Product handle
 * @private
 */
const extractHandleFromUrl = (url) => {
  if (!url) return "";

  // Cache handles to avoid repeated URL parsing
  if (!_handleCache) {
    _handleCache = new Map();
  }

  if (_handleCache.has(url)) {
    return _handleCache.get(url);
  }

  const urlParts = url.split("/");
  const handle = urlParts[urlParts.length - 1].split("?")[0];

  _handleCache.set(url, handle);
  return handle;
};

/**
* Transform a Nosto facet object to match Shopify filter structure
* @param {Object} nostoFacet - The Nosto facet object
* @param {Function} [urlBuilder] - Optional URL builder function (nostoField, value, action) => url
* @returns {Object|null} - Transformed filter object or null if should be skipped
* @private
*/
const transformFacetToFilter = (nostoFacet, urlBuilder) => {
  const shopifyParam = nostoMapFieldToFilter(nostoFacet.field);

  if (!shopifyParam) {
    return null; // Skip this facet
  }

  // Use the field property for accurate mapping, fallback to name
  const mappingKey = nostoFacet.field || nostoFacet.name;

  // Check if this is a price range facet or other special facet type
  if (!nostoFacet.data || !Array.isArray(nostoFacet.data)) {
    return null; // Skip facets without proper data array
  }

  const displayLabel = nostoTransformFilterLabel(nostoFacet.name, nostoFacet.field);

  return {
    activeValues: [],
    label: displayLabel,
    operator: "OR",
    presentation: "text",
    paramName: shopifyParam,
    type: "list",
    values: nostoFacet.data.map(item => ({
      active: item.selected || false,
      count: item.count || 0,
      label: item.value,
      paramName: shopifyParam,
      value: item.value,
      urlToAdd: urlBuilder ? urlBuilder(mappingKey, item.value, "add") : `/collections/all?${shopifyParam}=${encodeURIComponent(item.value)}&view=csr-json`,
      urlToRemove: urlBuilder ? urlBuilder(mappingKey, item.value, "remove") : "/collections/all?view=csr-json"
    }))
  };
};

/**
 * Get the default variant selection for a Nosto product based on availability, sorting, and selected filters
 * @param {Object} product - The Nosto product object
 * @param {string} sortBy - The sort option (e.g., 'price-ascending', 'price-descending', 'relevance')
 * @param {Object} selectedFilters - Object mapping filter keys to selected values (e.g., {'skus.customFields.greyson-color_family': ['Pink']})
 * @param {Object} spotlightConfig - Spotlight selection configuration
 * @param {boolean} prioritizeSaleVariants - Collection-level sale prioritization (greyson.prioritize_sale_variants)
 * @returns {{variantId: number, imageUrl: string}|null} - The selected variant payload or null if no available variants
 * @private
 */
const getDefaultVariant = (
  product,
  sortBy = 'relevance',
  selectedFilters = {},
  spotlightConfig = {},
  prioritizeSaleVariants = false,
  hasActiveSort = false,
) => {
  if (!product || !product.skus || !Array.isArray(product.skus)) {
    return null;
  }

  // Cache filter entries and create lookup maps for better performance
  const filterEntries = Object.entries(selectedFilters);
  const hasFilters = filterEntries.length > 0;
  const isPriceSorted = sortBy === 'price-ascending' || sortBy === 'price-descending';
  const isAscending = sortBy === 'price-ascending';
  const shouldUseSpotlight =
    spotlightConfig?.enabled === true &&
    !hasFilters &&
    !isPriceSorted;

  // Pre-process SKUs: filter, create lookup maps, and sort in a single pass
  const processedSkus = [];

  for (let i = 0; i < product.skus.length; i++) {
    const sku = product.skus[i];

    // Create custom fields lookup map for this SKU
    const customFieldsMap = new Map();
    if (sku.customFields) {
      for (let j = 0; j < sku.customFields.length; j++) {
        const field = sku.customFields[j];
        customFieldsMap.set(field.key, field.value);
      }
    }

    // Check availability - skip if not available
    if (customFieldsMap.get('greyson-availability') !== 'true') {
      continue;
    }

    // Apply selected filters if any
    if (hasFilters) {
      // console.log(`Applying selected filters for SKU ${sku.id} for ${product.name}`);
      let matchesAllFilters = true;

      for (const [filterField, selectedValues] of filterEntries) {
        if (!Array.isArray(selectedValues) || selectedValues.length === 0) continue;

        let fieldMatches = false;

        if (filterField.startsWith('skus.customFields.')) {
          const customFieldKey = filterField.slice(18); // 'skus.customFields.'.length = 18
          const fieldValue = customFieldsMap.get(customFieldKey);
          fieldMatches = fieldValue && selectedValues.includes(fieldValue);
        } else if (filterField.startsWith('skus.')) {
          const skuFieldKey = filterField.slice(5); // 'skus.'.length = 5
          fieldMatches = selectedValues.includes(sku[skuFieldKey]);
        } else {
          // Product-level filters don't exclude SKUs
          fieldMatches = true;
        }

        if (!fieldMatches) {
          matchesAllFilters = false;
          break;
        }
      }

      if (!matchesAllFilters) {
        // console.log(`Ignoring SKU ${sku.id} for ${product.name} because it does not match all filters`);
        continue;
      }
    }

    // Add to processed SKUs with pre-parsed price for sorting
    const processedSku = {
      id: sku.id,
      price: isPriceSorted ? (parseFloat(sku.price) || 0) : 0,
      customFieldsMap,
      imageUrl: nostoSetImageWidth(String(
        sku?.imageUrl || sku?.image_url || sku?.image || "",
      ).trim()),
    };

    processedSkus.push(processedSku);
  }

  // No SKU cleared the availability/filter gates. Fall back rather than
  // dropping the product from the grid entirely.
  if (processedSkus.length === 0) {
    //console.log(`No valid SKUs found for ${product.name}`);
    return getFallbackDefaultVariant(product);
  }

  let selectedVariantId = null;

  // A filter, or a sort carrying no price intent (featured, alphabetical,
  // newest), means shopper/merchant ordering decides — skip custom selection.
  // The default view and the two price sorts still run it.
  const sortAllowsSaleSelection = !hasActiveSort || isPriceSorted;

  if (!hasFilters && sortAllowsSaleSelection) {
    // Sale prioritization and spotlight are mutually exclusive — we run one or
    // the other, never both. Sale takes precedence, mirroring the card-side rule
    // in shouldUseSpotlightVariantSelection(), which bails when
    // shouldPrioritizeSaleVariants() is true.
    if (prioritizeSaleVariants) {
      // Which variant a card displays is separate from how the grid is ordered.
      // On a sale collection the card should lead with the discounted colour, so
      // only a shopper's own high→low choice asks for the highest-priced variant.
      // A collection whose default sort is already high→low is still the default
      // view, so it keeps the sale variant. hasActiveSort is false in that case,
      // because product-grid.js compares the sort against the collection default.
      //
      // Falls through to the SKU path when the metafield is absent or names a
      // variant missing from the feed.
      const wantHighestPricedVariant = hasActiveSort && isPriceSorted && !isAscending;
      selectedVariantId = nostoResolveSaleVariantId(
        product,
        processedSkus,
        wantHighestPricedVariant,
      );
    } else if (shouldUseSpotlight) {
      // Spotlight forward-faces specific colors.
      selectedVariantId = selectSpotlightVariantId(processedSkus, {
        spotlightColors: spotlightConfig?.colors,
        spotlightColorFamily: spotlightConfig?.colorFamily,
      });
    }
  }

  if (!Number.isFinite(selectedVariantId)) {
    // Sort if needed (in-place for better performance)
    if (isPriceSorted) {
      //console.log(`Price sorting SKUs for ${product.name}`);
      processedSkus.sort((a, b) => isAscending ? a.price - b.price : b.price - a.price);
    }

    //console.log(`Processed SKUs for ${product.name}:`, processedSkus);

    const firstSku = processedSkus[0];
    selectedVariantId = firstSku?.id ? parseInt(firstSku.id, 10) : null;
  }

  if (!Number.isFinite(selectedVariantId)) {
    return getFallbackDefaultVariant(product);
  }

  const selectedSku = processedSkus.find(
    (sku) => parseInt(sku?.id, 10) === parseInt(selectedVariantId, 10),
  );

  // The merchandised price-extreme variant may sit outside processedSkus (it
  // outranks the local availability gate), so fall back to a full-feed image
  // lookup rather than emitting an empty imageUrl.
  const imageUrl = String(selectedSku?.imageUrl || "").trim();

  return {
    variantId: parseInt(selectedVariantId, 10),
    imageUrl: imageUrl
      ? nostoSetImageWidth(imageUrl)
      : nostoGetVariantImageUrl(product, selectedVariantId),
  };
};

/**
 * Resolve a default variant when SKU-level selection produces nothing.
 *
 * Nosto's `skus` array carries variants regardless of Online Store publishing,
 * so a SKU-derived id can be invisible to Liquid (`?variant=` resolves to
 * nothing → product-card removes itself). These fallbacks trade that risk down:
 *
 *  1. `greyson-first_greyson_available_variant` — a product-level customField
 *     mirroring the Liquid metafield, delivered as a ProductVariant GID. It is
 *     authored from Liquid's view of the product, so it is publishing-aware and
 *     is the preferred recovery.
 *  2. First SKU in the array — last-ditch. NOT publishing-aware; the card's own
 *     `setDefaultVariant()` fallback chain is the net if this id is unpublished.
 *
 * @param {Object} product - Nosto product hit
 * @returns {{variantId: number, imageUrl: string}|null}
 */
const getFallbackDefaultVariant = (product = {}) => {
  const metafieldVariantId = nostoParseVariantGid(
    nostoGetCustomFieldValue(
      product?.customFields,
      "greyson-first_greyson_available_variant",
    ),
  );

  if (metafieldVariantId) {
    return {
      variantId: metafieldVariantId,
      imageUrl: nostoGetVariantImageUrl(product, metafieldVariantId),
    };
  }

  // Last-ditch: first SKU in the array, publishing unknown.
  const skus = Array.isArray(product?.skus) ? product.skus : [];
  for (let i = 0; i < skus.length; i++) {
    const firstVariantId = nostoGetSkuVariantId(skus[i]);
    if (!Number.isFinite(firstVariantId)) continue;
    return {
      variantId: firstVariantId,
      imageUrl: nostoGetVariantImageUrl(product, firstVariantId),
    };
  }

  return null;
};

/**
 * Extract selected filters from Nosto facets
 * @param {Array} facets - Array of Nosto facet objects
 * @returns {Object} - Object mapping Nosto field names to arrays of selected values
 * @private
 */
const extractSelectedFilters = (facets = []) => {
  const selectedFilters = {};

  // console.log(`Extracting selected filters from ${facets.length} facets`, facets);

  for (let i = 0; i < facets.length; i++) {
    const facet = facets[i];
    if (facet.data && Array.isArray(facet.data)) {
      const selectedValues = [];
      const facetData = facet.data;

      // Single pass through facet data
      for (let j = 0; j < facetData.length; j++) {
        const item = facetData[j];
        if (item.selected === true) {
          selectedValues.push(item.value);
        }
      }

      if (selectedValues.length > 0) {
        selectedFilters[facet.field] = selectedValues;
      }
    }
  }

  return selectedFilters;
};

const normalizeNostoValue = (value) => {
  if (value === null || value === undefined) return "";
  return handleize(String(value));
};

const getSkuFieldValue = (sku, key) => {
  if (!sku?.customFieldsMap || !key) return "";
  return sku.customFieldsMap.get(key) || "";
};

const selectSpotlightVariantId = (
  processedSkus = [],
  { spotlightColors = [], spotlightColorFamily = "" } = {},
) => {
  if (!Array.isArray(processedSkus) || processedSkus.length === 0) return null;

  const normalizedSpotlightColors = Array.isArray(spotlightColors)
    ? spotlightColors.map((color) => normalizeNostoValue(color)).filter(Boolean)
    : [];

  for (const spotlightColor of normalizedSpotlightColors) {
    const matchingColorSku = processedSkus.find((sku) => {
      const availableColor = normalizeNostoValue(
        getSkuFieldValue(sku, "greyson-available_color"),
      );
      const color = normalizeNostoValue(getSkuFieldValue(sku, "color"));
      return availableColor === spotlightColor || color === spotlightColor;
    });
    if (matchingColorSku?.id) {
      return parseInt(matchingColorSku.id, 10);
    }
  }

  const normalizedSpotlightColorFamily = normalizeNostoValue(spotlightColorFamily);
  if (normalizedSpotlightColorFamily) {
    const matchingFamilySku = processedSkus.find((sku) => {
      const availableColorFamily = normalizeNostoValue(
        getSkuFieldValue(sku, "greyson-available_color_family"),
      );
      const colorFamily = normalizeNostoValue(
        getSkuFieldValue(sku, "greyson-color_family"),
      );
      return (
        availableColorFamily === normalizedSpotlightColorFamily ||
        colorFamily === normalizedSpotlightColorFamily
      );
    });
    if (matchingFamilySku?.id) {
      return parseInt(matchingFamilySku.id, 10);
    }
  }

  const firstAvailableSku = processedSkus[0];
  if (!firstAvailableSku?.id) return null;
  return parseInt(firstAvailableSku.id, 10);
};

/**
 * Create empty results structure
 * @param {Object} config - Configuration options
 * @param {string} config.context - "search" or "collection"
 * @param {string} config.terms - Search terms
 * @returns {Object} - Empty Shopify-compatible structure
 * @private
 */
const createEmptyProductsStructure = ({ context, terms = "" }) => {
  return {
    products: [],
    filters: [],
    resultsCount: 0,
    paginate: {
      currentOffset: 0,
      currentPage: 1,
      items: 0,
      pageSize: 50,
      pages: 0,
      parts: [],
    },
    performed: true,
    terms: context === "search" ? terms : "",
    types: ["product"],
  };
};

/**
 * Parses a Nosto price field into a number.
 *
 * @param {*} value - A Nosto price value, string or number
 * @returns {number|null} The amount, or null when it is not a usable number
 */
export function nostoParseAmount(value) {
  if (value === null || value === undefined || value === "") return null;
  const amount = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(amount) ? amount : null;
}

/**
 * Resolves a product's compare-at price from the Nosto feed.
 *
 * Nosto's `product.price` is the highest variant price, not a compare-at, so
 * comparing it against a SKU price only reveals a price range. The real compare-at
 * arrives as the `greyson-available_compare_at_price_*` custom fields, which the
 * theme's own metafields feed.
 *
 * A variant is on sale when its own price is below that compare-at. Supplying this
 * lets an injected card paint its cross-out immediately, instead of showing a single
 * price that changes once the deferred product-json fetch lands.
 *
 * @param {Object} product - The Nosto product
 * @param {Object} [defaultSku] - The SKU backing the card's default variant
 * @returns {number|null} The compare-at amount, or null when the variant is not on sale
 */
export function nostoResolveCompareAtPrice(product, defaultSku) {
  const variantPrice = nostoParseAmount(defaultSku?.price);
  if (variantPrice === null) return null;

  // Prefer the max: a product whose compare-at varies by variant still crosses out
  // against the highest, matching how the price snippets read the metafield.
  const compareAtMoney = nostoParseMoneyField(
    product?.customFields,
    "greyson-available_compare_at_price_max_money",
  );
  const compareAt =
    nostoParseAmount(compareAtMoney?.amount) ??
    nostoParseAmount(
      nostoGetCustomFieldValue(product?.customFields, "greyson-available_compare_at_price_max"),
    );
  if (compareAt === null) return null;

  // Only a variant priced below its compare-at is actually marked down.
  if (compareAt <= variantPrice) return null;
  return compareAt;
}

/**
 * Transform Nosto response to Shopify format for product grids
 * @param {Object} nostoResponse - Raw Nosto response
 * @param {Object} config - Configuration options
 * @param {string} config.context - "search" or "collection"
 * @param {string} config.terms - Search terms (for search context)
 * @param {number} config.currentPage - Current page number
 * @param {number} config.pageSize - Items per page
 * @param {string} config.sortBy - Current sort option
 * @param {Object} config.spotlightConfig - Spotlight selection configuration
 * @param {boolean} config.prioritizeSaleVariants - Collection-level sale prioritization (greyson.prioritize_sale_variants)
 * @param {Function} config.generatePaginationParts - Function to generate pagination
 * @param {Function} config.getSortOptions - Function to get sort options
 * @returns {Object} - Shopify-compatible data structure
 */
export const nostoTransformToShopifyProducts = (nostoResponse, {
  context,
  terms = "",
  currentPage = 1,
  pageSize = 50,
  sortBy = "relevance",
  spotlightConfig = {},
  prioritizeSaleVariants = false,
  hasActiveSort = false,
  generatePaginationParts,
  getSortOptions
}) => {
  if (!nostoResponse?.products?.hits) {
    return createEmptyProductsStructure({ context, terms });
  }

  // Extract selected filters from Nosto facets
  const selectedFilters = extractSelectedFilters(nostoResponse.products?.facets || []);

  // Process products with single pass filtering and mapping
  const products = [];
  const hits = nostoResponse.products.hits;

  for (let i = 0; i < hits.length; i++) {
    const product = hits[i];

    const defaultVariantSelection = getDefaultVariant(
      product,
      sortBy,
      selectedFilters,
      spotlightConfig,
      prioritizeSaleVariants,
      hasActiveSort,
    );
    if (defaultVariantSelection?.variantId) {
      const defaultVariantId = parseInt(defaultVariantSelection.variantId, 10);
      const subtitle = nostoGetCustomFieldValue(product?.customFields, "custom-subtitle");

      // Card-visible enrichment (render-then-fetch): map Nosto's resolved
      // customFields/skus into the normalized CardPayload fields so the grid can
      // inject a near-complete card without the per-card product-json fetch.
      // GID-only metaobject fields (badges/promotions/order_type) are omitted —
      // the deferred product-json fetch supplies them.
      const availableColors = nostoParseJsonArrayField(product?.customFields, "greyson-available_colors");
      const saleColors = nostoParseJsonArrayField(product?.customFields, "greyson-sale_colors");
      const hideColors = nostoParseJsonArrayField(product?.customFields, "greyson-hide_colors");
      const priceMin = nostoParseMoneyField(product?.customFields, "greyson-available_price_min_money");
      const priceMax = nostoParseMoneyField(product?.customFields, "greyson-available_price_max_money");
      // Alternates only (not product.imageUrl): alternateImageUrls is where
      // Shopify's per-variant/reprocessed media actually lives, while
      // product.imageUrl is often a stale/generic asset that happens to share
      // the same skucode/colorcode filename prefix. nostoResolveColorImage
      // already falls back to fallbackUrl (= product.imageUrl) below when no
      // alternate matches, so excluding it here means "search alternates
      // first, fall back to main image" instead of main image winning ties.
      const mediaUrls = Array.isArray(product?.alternateImageUrls) ? product.alternateImageUrls : [];

      // Resolve the DEFAULT variant's SKU so the main image and the lead swatch
      // reflect the default color (e.g. WOLF BLUE / MCLSCK64-476-S), not Nosto's
      // arbitrary product-level imageUrl. Nosto now returns sku.imageUrl directly
      // (authoritative, variant-linked asset) so that wins first; skucode->media
      // filename matching is only a fallback for SKUs missing imageUrl.
      const defaultSku = nostoFindSkuByVariantId(product, defaultVariantId);
      const defaultColor =
        nostoGetCustomFieldValue(defaultSku?.customFields, "greyson-available_color") ||
        nostoGetCustomFieldValue(defaultSku?.customFields, "color");
      const defaultSkucode = nostoGetCustomFieldValue(defaultSku?.customFields, "skucode");
      const defaultSkuImageUrl = String(defaultSku?.imageUrl || "").trim();
      const imageUrl = (defaultSkuImageUrl || defaultSkucode)
        ? nostoResolveColorImage(defaultSkucode, mediaUrls, product?.imageUrl, 540, defaultSkuImageUrl)
        : (defaultVariantSelection.imageUrl || nostoGetVariantImageUrl(product, defaultVariantId));

      const swatches = nostoBuildCardSwatches(product, {
        saleColors,
        hideColors,
        mediaUrls,
        defaultColor,
        maxSwatches: 3,
      });

      products.push({
        id: parseInt(product.productId, 10),
        handle: extractHandleFromUrl(product.url),
        title: product.name,
        subtitle,
        url: product.url,
        imageUrl,
        defaultVariantId,
        tags: product.tags1,
        // CardPayload enrichment
        imageSrcset: null, // Nosto has a single URL; deferred fetch supplies real srcset
        imageSizes: null,
        priceHtml: null, // deferred fetch supplies real price_html
        priceRangeHtml: null,
        price: priceMin.amount,
        priceMax: priceMax.amount,
        compareAtPrice: nostoResolveCompareAtPrice(product, defaultSku),
        currency: priceMin.currency || priceMax.currency || "",
        variantPrice: nostoParseAmount(defaultSku?.price),
        availableColors,
        saleColors,
        hideColors,
        swatches,
      });
    }
  }

  const totalResults = nostoResponse.products.total || 0;
  const totalPages = Math.ceil(totalResults / pageSize);

  // Transform Nosto facets to Shopify filter format with single pass
  const transformedFilters = [];
  const facets = nostoResponse.products?.facets || [];
  for (let i = 0; i < facets.length; i++) {
    const facet = facets[i];
    try {
      const transformedFilter = transformFacetToFilter(facet);
      if (transformedFilter) {
        transformedFilters.push(transformedFilter);
      }
    } catch (error) {
      console.error('Failed to transform Nosto facet to Shopify filter:', error);
    }
  }

  return {
    products,
    filters: transformedFilters,
    resultsCount: totalResults,
    paginate: {
      currentOffset: (currentPage - 1) * pageSize,
      currentPage: currentPage,
      items: totalResults,
      pageSize: pageSize,
      pages: totalPages,
      parts: generatePaginationParts ? generatePaginationParts(totalPages, currentPage) : [],
    },
    performed: true,
    terms: context === "search" ? terms : "",
    types: ["product"],
    sortBy: sortBy,
    sortOptions: getSortOptions ? getSortOptions() : [],
    vendor: "nosto",
  };
};

/**
 * Transform Nosto response to Shopify format for filters only
 * @param {Object} nostoResponse - Raw Nosto response
 * @param {Object} config - Configuration options
 * @param {string} config.sortBy - Current sort option
 * @param {Function} config.getSortOptions - Function to get sort options
 * @param {Function} config.urlBuilder - Function to build filter URLs
 * @returns {Object} - Shopify-compatible filter structure
 */
export const nostoTransformToShopifyFilters = (nostoResponse, {
  sortBy = "relevance",
  getSortOptions,
  urlBuilder
}) => {
  const filters = [];
  const facets = nostoResponse.products?.facets || [];

  for (let i = 0; i < facets.length; i++) {
    const facet = facets[i];
    try {
      const transformedFilter = transformFacetToFilter(facet, urlBuilder);
      if (transformedFilter) {
        filters.push(transformedFilter);
      }
    } catch (error) {
      console.error('Failed to transform Nosto facet to Shopify filter:', error);
      const configError = new Error(`Nosto utilities failed to transform facet: ${facet.name || facet.field}`);
      throw _createNostoError('CONFIGURATION_ERROR', { facet: facet.name || facet.field }, configError);
    }
  }

  return {
    filters,
    sortBy: sortBy,
    sortOptions: getSortOptions ? getSortOptions() : [],
  };
};

/**
 * Transform Nosto response for predictive search
 * @param {Object} nostoResponse - Raw Nosto response
 * @returns {Object} - Predictive search compatible structure
 */
export const nostoTransformToPredictiveSearch = (nostoResponse) => {
  // Match Shopify predictive search format exactly
  const results = {
    vendor: "nosto",
    performed: true,
    resources: {
      articles: [],
      collections: [],
      pages: [],
      products: [],
      queries: []
    },
    terms: nostoResponse.query || "",
    types: []
  };

  // Transform products
  if (nostoResponse.products?.hits) {
    // Extract selected filters from Nosto facets (though typically predictive search may not have active filters)
    const selectedFilters = extractSelectedFilters(nostoResponse.products?.facets || []);

    // Process products with single pass, limiting to 10 items
    const products = [];
    const hits = nostoResponse.products.hits;
    const maxProducts = Math.min(hits.length, 10);

    for (let i = 0; i < maxProducts; i++) {
      const product = hits[i];

      const defaultVariantSelection = getDefaultVariant(product, 'relevance', selectedFilters);
      if (defaultVariantSelection?.variantId) {
        const defaultVariantId = parseInt(defaultVariantSelection.variantId, 10);
        // The image url will be a string such as: "https://cdn.shopify.com/s/files/1/0041/1018/8642/files/YFA24K13LS_476_1_0507.png?v=1734372346"
        const selectedVariantImageUrl =
          defaultVariantSelection.imageUrl ||
          nostoGetVariantImageUrl(product, defaultVariantId);
        const imageUrl = selectedVariantImageUrl
          ? selectedVariantImageUrl + '?v=' + Date.now() + '&width=330'
          : '';
        const imageHtml = imageUrl ?
          `<img src="${imageUrl}" alt="${product.name}" srcset="${imageUrl.replace('&width=330', '&width=165')} 165w" width="165" height="50" class="predictive-search__image">` :
          '';

        products.push({
          id: parseInt(product.productId, 10),
          handle: extractHandleFromUrl(product.url),
          title: product.name,
          url: product.url,
          imageUrl: imageUrl,
          imageHtml: imageHtml,
          defaultVariantId: parseInt(defaultVariantId, 10)
        });
      }
    }

    results.resources.products = products;
    results.types.push('product');
  }

  // Transform keywords/queries
  if (nostoResponse.keywords?.hits) {
    const searchTerm = (nostoResponse.query || "").toLowerCase();
    const queries = [];
    const hits = nostoResponse.keywords.hits;
    const maxQueries = Math.min(hits.length, 10);

    // Pre-compile regex if searchTerm exists for better performance
    let searchRegex = null;
    if (searchTerm) {
      const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      searchRegex = new RegExp(`(${escapedTerm})`, 'gi');
    }

    for (let i = 0; i < maxQueries; i++) {
      const keyword = hits[i];
      const keywordText = keyword.keyword;

      // Create styled_text by highlighting the search term
      let styledText = keywordText;
      if (searchRegex && keywordText.toLowerCase().includes(searchTerm)) {
        styledText = keywordText.replace(searchRegex, '<mark>$1</mark>');

        // Wrap non-highlighted parts in spans
        styledText = styledText.replace(/^([^<]+)(?=<mark>)/, '<span>$1</span>');
        styledText = styledText.replace(/(<\/mark>)([^<]+)(?=<mark>|$)/g, '$1<span>$2</span>');
      }

      queries.push({
        text: keywordText,
        styledText: styledText,
        url: `/search?q=${encodeURIComponent(keywordText)}`
      });
    }

    results.resources.queries = queries;
    results.types.push('query');
  }

  return results;
};
